/**
 * Web ClipboardCapabilities — navigator.clipboard read/write (page gesture)
 * + broadcastClip fan-out. SECURITY: never logs clip content.
 */
import type { ClipboardCapabilities } from "@reflecto/client-ui";
import { logWarn } from "../lib/log";
import { broadcastClip } from "../services/clipboard";
import { webStorage } from "./storage.web";

export function createWebClipboardCapabilities(): ClipboardCapabilities {
  return {
    readText: () => navigator.clipboard.readText(),
    writeText: (text) => navigator.clipboard.writeText(text),
    async broadcast(content, { sensitive }) {
      try {
        const { token, secretKey, selfDeviceName } = await webStorage.get(
          "token",
          "secretKey",
          "selfDeviceName"
        );
        // Explicit guard so the adapter is self-documenting + deterministic
        // (broadcastClip also throws this downstream, but fail here first).
        if (!token || !secretKey) throw new Error("not_paired");
        return await broadcastClip(token, secretKey, content, {
          originLabel: selfDeviceName,
          sensitive,
        });
      } catch (err) {
        // Tag only (never the clip content); re-throw so ClipDock surfaces its result line.
        // Skip the self-thrown `not_paired` guard — it's the normal unpaired case,
        // not a fanout failure, so logging it would be misleading.
        if ((err as Error).message !== "not_paired") {
          logWarn("clipboard", "clip_broadcast_failed", err);
        }
        throw err;
      }
    },
  };
}
